import { clsx } from "clsx";
import { useId, useRef, useState } from "react";

export const CHECKOUT_VERIFICATION_CODE_LENGTH = 4;

type Props = {
  value: string;
  onChange: (value: string) => void;
  invalid?: boolean;
  disabled?: boolean;
};

export function CheckoutVerificationCodeField({
  value,
  onChange,
  invalid = false,
  disabled = false,
}: Props) {
  const id = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);
  const activeIndex = Math.min(
    value.length,
    CHECKOUT_VERIFICATION_CODE_LENGTH - 1,
  );

  return (
    <div className="relative w-full max-w-[300px]">
      <label htmlFor={id} className="sr-only">
        Verification code
      </label>
      <input
        ref={inputRef}
        id={id}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="one-time-code"
        maxLength={CHECKOUT_VERIFICATION_CODE_LENGTH}
        value={value}
        disabled={disabled}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        onChange={(event) => {
          onChange(
            event.target.value
              .replace(/\D/g, "")
              .slice(0, CHECKOUT_VERIFICATION_CODE_LENGTH),
          );
        }}
        aria-invalid={invalid ? true : undefined}
        className="absolute inset-0 z-10 size-full cursor-text font-sans text-base font-normal opacity-0"
      />
      <div
        className="grid grid-cols-4 gap-3"
        aria-hidden
        onClick={() => inputRef.current?.focus()}
      >
        {Array.from({ length: CHECKOUT_VERIFICATION_CODE_LENGTH }).map(
          (_, index) => {
            const digit = value[index];
            const active = focused && index === activeIndex;
            return (
              <span
                key={index}
                className={clsx(
                  "flex h-16 items-center justify-center rounded-[9px] border bg-white font-sans text-base font-normal text-butter-green transition-colors",
                  invalid
                    ? "border-butter-pink"
                    : active
                      ? "border-butter-green/60"
                      : digit
                        ? "border-butter-green/25"
                        : "border-butter-cream",
                )}
              >
                {digit}
              </span>
            );
          },
        )}
      </div>
    </div>
  );
}
